/**
 * Video Preloader Utility
 * 
 * Implements aggressive preloading strategy for HLS and MP4 videos
 * to achieve <5ms render-to-play latency after CDN fetch.
 * 
 * Features:
 * - Pre-fetches and parses HLS manifests
 * - Pre-initializes HLS.js instances
 * - Pre-fetches first video segments
 * - Video element pooling for reuse
 */

import Hls from "hls.js";

// Video element pool for reuse
const videoElementPool: HTMLVideoElement[] = [];
const MAX_POOL_SIZE = 5;

// HLS instance cache - pre-initialized instances ready to attach
// Stores both HLS instance and the video element it's attached to
const hlsInstanceCache = new Map<string, { hls: Hls; video: HTMLVideoElement; manifestParsed: boolean }>();

// Manifest cache - pre-parsed manifest data
const manifestCache = new Map<string, string>();

// Prefetch link elements for native browser preloading
const prefetchLinks = new Map<string, HTMLLinkElement>();

/**
 * Check if a URL is cross-origin
 */
function isCrossOrigin(url: string): boolean {
  try {
    const urlObj = new URL(url, window.location.href);
    return urlObj.origin !== window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Get or create a video element from the pool
 */
export function getPooledVideoElement(): HTMLVideoElement {
  if (videoElementPool.length > 0) {
    return videoElementPool.pop()!;
  }
  const video = document.createElement("video");
  video.preload = "auto";
  video.playsInline = true;
  video.muted = true; // Muted for preloading
  video.style.display = "none";
  document.body.appendChild(video);
  return video;
}

/**
 * Return a video element to the pool
 */
export function returnVideoElementToPool(video: HTMLVideoElement): void {
  if (videoElementPool.length >= MAX_POOL_SIZE) {
    video.remove();
    return;
  }
  // Reset video element
  video.pause();
  video.currentTime = 0;
  video.src = "";
  video.load();
  videoElementPool.push(video);
}

/**
 * Prefetch HLS manifest and parse it
 * Skips cross-origin URLs to avoid CORS errors
 */
async function prefetchHlsManifest(url: string): Promise<void> {
  // Skip cross-origin URLs - fetch() cannot access them without CORS headers
  if (isCrossOrigin(url)) {
    return;
  }

  if (manifestCache.has(url)) {
    return; // Already cached
  }

  try {
    const response = await fetch(url);
    if (!response.ok) return;
    
    const manifestText = await response.text();
    manifestCache.set(url, manifestText);
    
    // Extract first segment URL for pre-fetching
    const lines = manifestText.split("\n");
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line && !line.startsWith("#") && line.endsWith(".ts")) {
        // Found a segment URL
        const segmentUrl = new URL(line, url).href;
        // Only prefetch if same-origin
        if (!isCrossOrigin(segmentUrl)) {
          fetch(segmentUrl, { method: "HEAD" }).catch(() => {});
        }
        break;
      }
    }
  } catch {
    // Silently fail - prefetching is optional
    // Only log in development to avoid console noise
    if (process.env.NODE_ENV === "development") {
      console.warn("Error prefetching HLS manifest (expected for cross-origin):", url);
    }
  }
}

/**
 * Pre-initialize HLS.js instance for a video URL
 * Skips cross-origin URLs - HLS.js can handle them natively without pre-initialization
 */
function preInitializeHls(url: string): void {
  // Skip cross-origin URLs - HLS.js will handle them when needed
  // Pre-initialization is mainly beneficial for same-origin resources
  if (hlsInstanceCache.has(url) || !Hls.isSupported() || isCrossOrigin(url)) {
    return;
  }

  const video = getPooledVideoElement();
  const hls = new Hls({
    enableWorker: true,
    maxBufferLength: 1, // Reduced for low latency
    maxBufferSize: 0,
    maxBufferHole: 0.3, // Reduced from 0.5
    lowLatencyMode: true,
    startLevel: -1, // Auto-select best quality
    capLevelToPlayerSize: true,
  });

  let manifestParsed = false;

  hls.on(Hls.Events.MANIFEST_PARSED, () => {
    manifestParsed = true;
    // Start loading immediately for faster startup
    hls.startLoad();
  });

  hls.on(Hls.Events.ERROR, (_event, data) => {
    if (data.fatal) {
      hls.destroy();
      hlsInstanceCache.delete(url);
      returnVideoElementToPool(video);
    }
  });

  hls.loadSource(url);
  hls.attachMedia(video);

  hlsInstanceCache.set(url, { hls, video, manifestParsed });
}

/**
 * Create native preload link for browser preloading
 */
function createPreloadLink(url: string, type: "video" | "manifest"): void {
  if (prefetchLinks.has(url)) {
    return;
  }

  const link = document.createElement("link");
  link.rel = "prefetch";
  link.as = type === "video" ? "video" : "fetch";
  link.href = url;
  link.crossOrigin = "anonymous";
  document.head.appendChild(link);
  prefetchLinks.set(url, link);
}

/**
 * Preload a single video URL
 * Skips cross-origin URLs to avoid CORS errors - native video player handles them
 */
export async function preloadVideo(url: string): Promise<void> {
  if (!url) return;

  // Skip cross-origin URLs - native video player can handle them directly
  // Preloading with fetch() requires CORS headers which CDN may not provide
  if (isCrossOrigin(url)) {
    // For cross-origin, just create a preload link (browser handles CORS for <link>)
    const isHls = /\.m3u8($|\?)/i.test(url);
    createPreloadLink(url, isHls ? "manifest" : "video");
    return;
  }

  const isHls = /\.m3u8($|\?)/i.test(url);

  if (isHls) {
    // Prefetch manifest (same-origin only)
    await prefetchHlsManifest(url);
    
    // Pre-initialize HLS instance (same-origin only)
    preInitializeHls(url);
    
    // Create preload link
    createPreloadLink(url, "manifest");
  } else {
    // For MP4/WebM, create preload link
    createPreloadLink(url, "video");
    
    // Preload video element (same-origin only)
    const video = getPooledVideoElement();
    video.src = url;
    video.load();
    
    // Return to pool after a short delay
    setTimeout(() => {
      returnVideoElementToPool(video);
    }, 5000);
  }
}

/**
 * Preload multiple video URLs (next 2-3 videos in feed)
 */
export async function preloadVideos(urls: string[]): Promise<void> {
  // Preload in parallel, but limit concurrent requests
  const batchSize = 3;
  for (let i = 0; i < urls.length; i += batchSize) {
    const batch = urls.slice(i, i + batchSize);
    await Promise.allSettled(batch.map((url) => preloadVideo(url)));
  }
}

/**
 * Get pre-initialized HLS instance if available
 * Returns the HLS instance ready to attach to a video element
 */
export function getPreInitializedHls(url: string): Hls | null {
  const cached = hlsInstanceCache.get(url);
  if (!cached || !cached.manifestParsed) {
    return null;
  }

  // Get the pre-initialized HLS instance
  const { hls, video } = cached;
  hlsInstanceCache.delete(url); // Remove from cache as it's now in use

  // Detach from pre-warmed video element and return it to pool
  if (hls.media) {
    hls.detachMedia();
  }
  returnVideoElementToPool(video);
  
  // Return HLS instance ready to attach to the React component's video element
  return hls;
}

/**
 * Clean up preloader resources
 */
export function cleanupPreloader(): void {
  // Clean up HLS instances
  hlsInstanceCache.forEach(({ hls, video }) => {
    hls.destroy();
    returnVideoElementToPool(video);
  });
  hlsInstanceCache.clear();

  // Clean up video elements
  videoElementPool.forEach((video) => {
    video.remove();
  });
  videoElementPool.length = 0;

  // Clean up prefetch links
  prefetchLinks.forEach((link) => {
    link.remove();
  });
  prefetchLinks.clear();

  // Clear manifest cache
  manifestCache.clear();
}

